import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Minus, Plus, Tag, Trash2, Truck, X } from "lucide-react";
import { toast } from "sonner";
import { useStore } from "@/context/StoreContext";
import { api, inr } from "@/lib/api";

const FREE_SHIP = 999;

export default function CartDrawer() {
  const { drawerOpen, setDrawerOpen, cart, updateQty, removeItem, subtotal, coupon, setCoupon } = useStore();
  const [code, setCode] = useState("");
  const [checking, setChecking] = useState(false);
  const navigate = useNavigate();

  const progress = Math.min(100, (subtotal / FREE_SHIP) * 100);
  const discount = coupon?.discount || 0;

  const applyCoupon = async () => {
    if (!code.trim()) return;
    setChecking(true);
    try {
      const res = await api.validateCoupon({ code, subtotal });
      if (res.valid) {
        setCoupon(res);
        toast.success(`Coupon applied — you save ${inr(res.discount)}`);
      } else {
        setCoupon(null);
        toast.error(res.message);
      }
    } catch {
      toast.error("Could not validate coupon right now.");
    } finally {
      setChecking(false);
    }
  };

  return (
    <AnimatePresence>
      {drawerOpen && (
        <>
          <motion.div
            data-testid="cart-overlay"
            className="fixed inset-0 z-[140] bg-black/60 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setDrawerOpen(false)}
          />
          <motion.aside
            data-testid="cart-drawer"
            className="fixed right-0 top-0 z-[150] flex h-full w-full max-w-md flex-col glass"
            style={{ background: "var(--mn-bg2)" }}
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 300, damping: 32 }}
          >
            <div className="flex items-center justify-between border-b px-6 py-5" style={{ borderColor: "var(--mn-glass-border)" }}>
              <h2 className="font-serif text-xl">Your Ritual Bag</h2>
              <button data-testid="cart-close-button" onClick={() => setDrawerOpen(false)} aria-label="Close cart" className="btn-outline-glow flex h-9 w-9 items-center justify-center rounded-full">
                <X size={16} />
              </button>
            </div>

            <div className="border-b px-6 py-4" style={{ borderColor: "var(--mn-glass-border)" }}>
              {subtotal >= FREE_SHIP ? (
                <p className="flex items-center gap-2 text-sm" style={{ color: "var(--mn-mint)" }} data-testid="free-shipping-unlocked">
                  <Truck size={15} /> Free shipping unlocked + 2 complimentary minis
                </p>
              ) : (
                <p className="text-sm" style={{ color: "var(--mn-text2)" }} data-testid="free-shipping-progress-text">
                  Add {inr(FREE_SHIP - subtotal)} more for free shipping
                </p>
              )}
              <div className="mt-2 h-1.5 overflow-hidden rounded-full" style={{ background: "var(--mn-surface)" }}>
                <motion.div
                  className="h-full rounded-full"
                  style={{ background: "linear-gradient(90deg, var(--mn-mint), var(--mn-gold))" }}
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 0.5 }}
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4">
              {cart.length === 0 ? (
                <div className="flex h-full flex-col items-center justify-center text-center" data-testid="cart-empty">
                  <p className="font-serif text-lg" style={{ color: "var(--mn-text2)" }}>Your bag is empty.</p>
                  <p className="mt-1 text-sm" style={{ color: "var(--mn-text2)" }}>Your skin ritual awaits.</p>
                  <button
                    data-testid="cart-shop-now"
                    onClick={() => { setDrawerOpen(false); navigate("/shop"); }}
                    className="btn-gold mt-6 rounded-full px-6 py-3 text-sm"
                  >
                    Explore the Collections
                  </button>
                </div>
              ) : (
                <ul className="space-y-4">
                  {cart.map((item) => (
                    <motion.li
                      key={item.key}
                      layout
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      className="glass-card flex gap-4 rounded-2xl p-3"
                      data-testid={`cart-item-${item.slug}`}
                    >
                      <img src={item.image} alt={item.name} className="h-20 w-16 rounded-xl object-cover" loading="lazy" />
                      <div className="flex flex-1 flex-col">
                        <p className="font-serif text-sm leading-snug">{item.name}</p>
                        <p className="mt-0.5 font-mono text-[0.65rem] uppercase tracking-wider" style={{ color: "var(--mn-text2)" }}>
                          {item.variant} · {item.collection}
                        </p>
                        <div className="mt-auto flex items-center justify-between pt-2">
                          <div className="flex items-center gap-2 rounded-full border px-2 py-1" style={{ borderColor: "var(--mn-glass-border)" }}>
                            <button data-testid={`qty-minus-${item.slug}`} onClick={() => updateQty(item.key, item.qty - 1)} aria-label="Decrease quantity"><Minus size={12} /></button>
                            <span className="w-5 text-center font-mono text-xs" data-testid={`qty-${item.slug}`}>{item.qty}</span>
                            <button data-testid={`qty-plus-${item.slug}`} onClick={() => updateQty(item.key, item.qty + 1)} aria-label="Increase quantity"><Plus size={12} /></button>
                          </div>
                          <span className="font-mono text-sm" style={{ color: "var(--mn-gold)" }}>{inr(item.price * item.qty)}</span>
                        </div>
                      </div>
                      <button data-testid={`remove-${item.slug}`} onClick={() => removeItem(item.key)} aria-label="Remove item" className="self-start opacity-50 transition-opacity hover:opacity-100">
                        <Trash2 size={15} />
                      </button>
                    </motion.li>
                  ))}
                </ul>
              )}
            </div>

            {cart.length > 0 && (
              <div className="border-t px-6 py-5" style={{ borderColor: "var(--mn-glass-border)" }}>
                <div className="flex gap-2">
                  <div className="flex flex-1 items-center gap-2 rounded-full border px-4" style={{ borderColor: "var(--mn-glass-border)" }}>
                    <Tag size={14} style={{ color: "var(--mn-gold)" }} />
                    <input
                      data-testid="coupon-input"
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      placeholder="Coupon code"
                      className="w-full bg-transparent py-2.5 text-sm outline-none placeholder:text-[var(--mn-text2)]"
                    />
                  </div>
                  <button data-testid="coupon-apply-button" onClick={applyCoupon} disabled={checking} className="btn-outline-glow rounded-full px-4 text-sm">
                    {checking ? "…" : "Apply"}
                  </button>
                </div>
                <div className="mt-4 space-y-1.5 text-sm">
                  <div className="flex justify-between" style={{ color: "var(--mn-text2)" }}>
                    <span>Subtotal</span>
                    <span data-testid="cart-subtotal">{inr(subtotal)}</span>
                  </div>
                  {discount > 0 && (
                    <div className="flex justify-between" style={{ color: "var(--mn-mint)" }} data-testid="cart-discount-row">
                      <span>Coupon {coupon.code}</span>
                      <span>−{inr(discount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between border-t pt-2 font-serif text-lg" style={{ borderColor: "var(--mn-glass-border)" }}>
                    <span>Total</span>
                    <span style={{ color: "var(--mn-gold)" }} data-testid="cart-total">{inr(subtotal - discount)}</span>
                  </div>
                </div>
                <button
                  data-testid="checkout-button"
                  onClick={() => { setDrawerOpen(false); navigate("/checkout"); }}
                  className="btn-gold mt-4 flex w-full items-center justify-center gap-2 rounded-full py-3.5 text-sm"
                >
                  Proceed to Checkout <ArrowRight size={16} />
                </button>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
